import type { IntakeMarketConfig, MarketIntakeSnapshot, MarketField } from "./types.js";

export const dbk_usEstimateConfigVersion = "erg-dbk-intake-us-v1-estimate-v5";
export const dbk_usEstimateSchemaVersion = "erg-dbk-intake-us-schema-v1-estimate-v5";
const retiredService = "AI accounting agent workflows";
const retiredDescription = "AI agents can help organize records, prepare accounting work, identify exceptions, and support review workflows.";
const retiredProviderNotice = "Professional provider-assisted services will become available as we expand our service options.";

export function dbk_parseUsIntakeVersion(value: string, schema = false) {
  const prefix = schema ? "erg-dbk-intake-us-schema-v" : "erg-dbk-intake-us-v";
  const match = value.startsWith(prefix) ? value.slice(prefix.length).match(/^([1-9]\d*)(?:-estimate-v([1-9]\d*))?$/) : null;
  if (!match) return null;
  const generation = Number(match[1]);
  const estimate = Number(match[2] ?? 0);
  return Number.isSafeInteger(generation) && Number.isSafeInteger(estimate) ? { generation, estimate } : null;
}

export function dbk_isIncreasingUsIntakeVersion(next: string, current: string, schema = false) {
  const a = dbk_parseUsIntakeVersion(next, schema);
  const b = dbk_parseUsIntakeVersion(current, schema);
  return !!a && !!b && (a.generation > b.generation || (a.generation === b.generation && a.estimate > b.estimate));
}

type ServiceConfig = { fields: readonly Pick<MarketField, "key" | "options">[] };
function retiredValues(config?: ServiceConfig) {
  return new Set([retiredService, ...(config?.fields.find(field => field.key === "requested_services")?.options ?? [])
    .filter(option => option.label === retiredService).map(option => option.value)]);
}

// Exact service-only migration. Unknown/invalid answers remain for the validator;
// no other field, order, duplicate, Yearly answer or administrator answer is lost.
export function dbk_normalizeUsEstimateValues<T extends Record<string, unknown>>(values: T, config?: ServiceConfig): T {
  const selected = values.requested_services;
  if (!Array.isArray(selected)) return { ...values };
  const retired = retiredValues(config);
  return { ...values, requested_services: selected.filter(value => !retired.has(value)) };
}

export function dbk_normalizeUsEstimateFields(fields: MarketField[]): MarketField[] {
  const retired = retiredValues({ fields });
  return fields.map(field => {
    const copy = structuredClone(field);
    if (copy.key === "report_frequency" && copy.kind === "select" && copy.options && !copy.options.some(option => option.value === "Yearly")) {
      const index = copy.options.findIndex(option => option.value === "Not sure");
      copy.options.splice(index < 0 ? copy.options.length : index, 0, { value: "Yearly", label: "Yearly" });
    }
    if (copy.key === "requested_services") {
      copy.options = copy.options?.filter(option => !retired.has(option.value));
      if (copy.help) copy.help = copy.help.replace(retiredDescription, "").replace(retiredProviderNotice, "").trim();
    }
    return copy;
  });
}

// A read-time projection, never a write/migration of administrator configuration.
// Preserve its base generation and any newer estimate revision. Management keeps
// the stored version for If-Match; only the public form sees the effective version.
export function dbk_buildUsEstimateConfig(base: IntakeMarketConfig): IntakeMarketConfig {
  const config = structuredClone(base);
  if (config.country_code !== "US") return config;
  const version = dbk_parseUsIntakeVersion(config.config_version);
  const schema = dbk_parseUsIntakeVersion(config.schema_version, true);
  if (!version || !schema) return config;
  config.config_version = `erg-dbk-intake-us-v${version.generation}-estimate-v${Math.max(5, version.estimate)}`;
  config.schema_version = `erg-dbk-intake-us-schema-v${schema.generation}-estimate-v${Math.max(5, schema.estimate)}`;
  config.fields = dbk_normalizeUsEstimateFields(config.fields);
  return config;
}

// Historical versions and the pinned fields remain historical. Only their view
// and a customer-confirmed correction normalize retired selections / Yearly.
export function dbk_normalizeUsEstimateSnapshot(snapshot: MarketIntakeSnapshot): MarketIntakeSnapshot {
  const copy = structuredClone(snapshot);
  if (copy.country_code !== "US") return copy;
  copy.fields = dbk_normalizeUsEstimateValues(copy.fields, copy.config_snapshot);
  copy.config_snapshot.fields = dbk_normalizeUsEstimateFields(copy.config_snapshot.fields);
  return copy;
}
